import React from "react";

import { processImagePath } from "../../../../util/Functions";

import mediumZoom from 'medium-zoom/dist/pure'
import 'medium-zoom/dist/style.css'
import './ProductMedia.scss';


import "../InnerImageZoom.scss";

export default function ProductMedia({ media }) {
	const vidRef = React.useRef(null);
	const mediaRef = React.useRef(null);
	const bodyRef = React.useRef(null);
	const scrollSnapSet = React.useRef(null);
	
	const currentMediaIndex = parseInt(new URL(window.location.href).searchParams.get("media-index")) || 0;
	
	React.useEffect(() => {
		// keep active media item when scrolling and changing orientations
		if (!scrollSnapSet.current && mediaRef.current) {
			scrollSnapSet.current = true;
			let orientationChanged = false;
			// NOTE: snap events not fully supported
			bodyRef.current?.addEventListener("scrollsnapchange", e => {
				if (!orientationChanged && mediaRef?.current) {
					// remove actives
					Array.from(mediaRef.current.querySelectorAll(".active"))
						.forEach(it => it.classList.remove("active"));

					// select media
					const newActive = e.snapTargetInline;
					newActive.classList.add("active");
					// select menu item
					const mediaId = newActive.dataset.media;
					mediaRef.current
						.querySelector(`.menu .menu-item[data-media="${mediaId}"]`)?.classList.add("active");
				}
				orientationChanged = false;
			});

			window.screen.orientation?.addEventListener("change", e => {
				const active = bodyRef.current?.querySelector(".active");
				if (active) {
					orientationChanged = true;
					active.scrollIntoView();
				}
			});
		}
	});

	const handleVideoClick = () => vidRef.current.requestFullscreen();

	const handleMediaHover = React.useCallback(e => {
		Array.from(mediaRef.current.querySelectorAll('.active'))
			.forEach(match => match.classList.remove('active'));
		Array.from(mediaRef.current.querySelectorAll(`[data-ref="${e.target.dataset.ref}"]`))
			.forEach(match => match.parentNode.classList.add('active'));

		const currentUrl = new URL(window.location.href);
		currentUrl.searchParams.set("media-index", e.target.parentNode.dataset.index);
		window.history.pushState(null, null, currentUrl.toString());
	}, []);

	const doNothing = React.useCallback(() => { }, []);

	const onMediaLoadError = React.useCallback((type, e) => {
		console.warn(`Failed to load product ${type}`, e.target.src);
		e.target.classList.add("image-error");
	}, []);

	const onImageLoad = React.useCallback((options, e) => {
		!options.onClick && mediumZoom(e.target, { margin: 0, });
		e.target.setAttribute("loaded", "");
	}, []);


	/**
	 * @param options.media
	 * @param {Function} [options.onClick]
	 * @param {Function} [options.onMouseOver]
	 * @param options.key
	 */
	const processImage = options => {
		return (
			<img
				alt=""
				src={processImagePath(options.media.source, "zoom")}
				data-zoom={!options.onClick}
				data-ref={options.media.source}
				onError={e => onMediaLoadError("image", e)}
				onClick={options.onClick || doNothing}
				onMouseOver={options.onMouseOver || doNothing}
				onLoad={e => onImageLoad(options, e)}
			/>
		);
	}

	/**
	 * @param options.media
	 * @param {Function} [options.onClick]
	 * @param {Function} [options.onMouseOver]
	 * @param options.key
	 * @param {Number} [options.delay]
	 */
	const processVideo = options => {
		return (
			<video
				ref={vidRef}
				autoPlay
				// loop
				muted
				onMouseOver={options.onMouseOver || doNothing}
				onClick={options.onClick || doNothing}
				onEnded={e => setTimeout(() => vidRef.current?.play(), options.delay ?? 0)}
				playsInline
				className="d-block w-100"
				src={processImagePath(options.media.source)}
				onError={e => onMediaLoadError("video", e)}
			/>
		);
	}

	/*
		<Note />
		component-ize so React knows {key} is set for each item
		without it directly in the .map() it's not smart enough to know {key} is there
	 */
	const MenuItem = React.useCallback(({ media, index, active }) => {
		return (
			<div className={["menu-item", active && "active"].filter(c=>c).join(" ")} data-media={media.id} data-index={index}>
				{!media.type || media.type === "image"
					? processImage({ media, onMouseOver: handleMediaHover })
					: processVideo({ media, onMouseOver: handleMediaHover, delay: 5000 * Math.random() })
				}
			</div>
		);
	}, []);

	const MediaItem = React.useCallback(({ media, id, index, active }) => {
		return (
			<div className={["media-item", active && "active"].filter(c=>c).join(" ")} data-media={media.id ?? id}>
				{
					!media.type || media.type === "image"
						? processImage({ media })
						: processVideo({ media, onClick: handleVideoClick })
				}
			</div>
		);
	}, []);

	media = media.filter(m => m.source);
	if (!media?.length) return (<></>);

	return (
		<div data-component="ProductMedia" className="product-media" ref={mediaRef}>
			<div className="menu">
				{media?.map((media, key) => <MenuItem key={key} index={key} media={media} active={key === currentMediaIndex} />)}
			</div>
			<div className="body" ref={bodyRef}>
				{media?.map((media, key) => <MediaItem key={key} media={media} index={key} id={media.id ?? key} active={key === currentMediaIndex} />)}
			</div>
		</div>
	);
}
